import { P2GText, P2GButtonGroup, P2GButtonGroupItem, useP2GMediaQueryOnly, P2GDropdown } from '@parcel2go/core-ui';
import { useContext, useEffect, useState } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import {
  AddNewItemAction,
  ArrangeReturnCollectionAction,
  UpdateFilterAction
} from '../../../lib/context/SmartSendContextModels';
import { getNamedFilter } from '../../../lib/helpers/DefaultFilters';
import { EditType } from '../item/EditType';
import { ItemOrderTrackingPanel } from '../panels/ItemOrderTrackingPanel';
import SetMarketplaceAction, { ItemActionType } from '../item/SetMarketplaceAction';
import { GenericEditProps } from './Edit';
import { OrderActionsPanel } from '../panels/OrderActionsPanel';
import { ItemOrderPanel } from '../panels/ItemOrderPanel';
import { Consts, EditEventType } from '../../../lib/em/EmitTypes';
import { useEventEmitter } from '../../../lib/em/Hooks';
import { uid } from '../../../lib/helpers/uid';
import ItemRefunds from './ItemRefunds';
import Loading from '../../../../../components/loading/Loading';
import { GetExternalTrackingNumber } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { EventSections } from '../../../lib/events/EventLogger';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import SelectOrCopy from '../item/SelectOrCopy';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import useCreateItemsHelper from '~/apps/smartsend/lib/helpers/useItemCreateHelper';
import useGoto from '~/hooks/use-goto';
import { SmartSendProvider } from '~/lib/consts/providers';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { SmartSendSubType } from '~/apps/smartsend/lib/consts/SmartSendSubType';
import OrderLabelsPanel from './OrderLabelsPanel';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { useNavExperience } from '~/components/nav/nav-experience';
import useOrderProcessLinks from '~/hooks/use-order-process-links';
import ItemClaim from './ItemClaim';

interface props extends GenericEditProps {
  getRawItem: (id: string) => SmartSendItemApiModel;
  onCancel: () => void;
  onRaiseEnquiry: () => void;
}

export default function OrderDetails(props: props) {
  const isXs = useP2GMediaQueryOnly('xs');
  const { createItemsHelper } = useCreateItemsHelper();
  const { getFullOrder, getOrderActions, getOrderTracking } = useSmartSendApi();
  const ctx = useContext(SmartSendContext);
  const [item, setItem] = useState<SmartSendItemApiModel>(null);
  const trackingNumber = item && GetExternalTrackingNumber(item);
  const { emit } = useEventEmitter<EditEventType>();
  const [loading, setLoading] = useState(true);
  const { goTo } = useGoto();
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const experience = useNavExperience();
  const { goToBuyAgain, goToReturn } = useOrderProcessLinks();

  useEffect(() => {
    track({
      type: 'edit_item_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.BookedItems, 'order-details-panel']),
        action: props.editType
      }
    });
  }, [props.editType]);

  useEffect(() => {
    // Try and get the full object from the API.
    const getData = async () => {
      setLoading(true);
      const o = await getFullOrder(SmartSendProvider, SmartSendProvider, props.item.Id);
      if (o.success) {
        setItem(o.result.Order);
      } else {
        setItem({ ...props.item });
      }
      setLoading(false);
    };
    if (props.item) {
      getData();
    } else {
      setItem(null);
    }
  }, [props.item?.Id]);

  const changeTab = (tab: EditType) => {
    emit(Consts.Edit, { Id: item.Id, Action: tab });
  };

  const buyAgain = async (item: SmartSendItemApiModel) => {
    // Consumer experience: hand off to the order-process (checkout) Buy Again flow by order line id,
    // keeping the customer in My Account's sibling app instead of dropping them into SmartSend. If the
    // order line id is missing, fall through to the existing SmartSend behaviour rather than no-op.
    if (experience === 'consumer' && item.BookingDetails?.OrderLineId) {
      goToBuyAgain(item.BookingDetails.OrderLineId);
      return;
    }

    const itemToAdd: SmartSendItemApiModel = JSON.parse(JSON.stringify(item));

    itemToAdd.Id = null;
    itemToAdd.Source = { Id: 'manual', Name: 'Manual', AccountId: null };
    itemToAdd.Status = 'Active';
    itemToAdd.MarketplaceStatus = 'Sold';
    itemToAdd.BookingDetails = null;
    itemToAdd.TrackingSummary = null;

    itemToAdd.MarketplaceOrderLines.forEach((i) => {
      i.Source = itemToAdd.Source;
      i.Id = null;
      i.MarketplaceOrderId = null;
      i.MarketplaceOrderLineId = null;
      i.InternalReference = null;
      i.CustomerReference = null;
      i.Status = itemToAdd.Status;
      i.MarketplaceStatus = itemToAdd.MarketplaceStatus;
    });

    itemToAdd.Description = itemToAdd.MarketplaceOrderLines[0]?.Product?.Description || 'New Item';

    itemToAdd.Tags.push('Reorder');

    createItemsHelper(
      [{ id: uid(), item: itemToAdd, raw: [] }],
      (items) => {
        const item = items[0];
        ctx.dispatch(new AddNewItemAction(item, !item.Service?.Id));
        ctx.dispatch(
          new UpdateFilterAction(SmartSendSubType, {
            ...ctx.state.filters[SmartSendSubType].filter,
            CurrentFilter: getNamedFilter('Post_Create')
          })
        );
      },
      () => {},
      () => {
        goTo(RoutingConstants.SmartSend);
        props.onClose();
      }
    );
  };

  const arrangeReturn = async (item: SmartSendItemApiModel) => {
    // Consumer experience: hand off to the order-process (checkout) return flow by order line id
    // instead of creating a return order in SmartSend. If the order line id is missing, fall through
    // to the existing SmartSend behaviour rather than no-op.
    if (experience === 'consumer' && item.BookingDetails?.OrderLineId) {
      goToReturn(item.BookingDetails.OrderLineId);
      return;
    }

    ctx.dispatch(new ArrangeReturnCollectionAction([item.Id]));
    goTo(RoutingConstants.SmartSend);
  };

  const setAction = (type: ItemActionType) => {
    SetMarketplaceAction(ctx.dispatch, props.item.Id, type);
    const i = props.getRawItem(props.item.Id);
    setItem(Object.assign(item, i));
  };

  if (loading || !item) {
    return <Loading />;
  }

  return (
    <SidePanelSections>
      <ItemRefunds bookingDetails={item?.BookingDetails} />
      <ItemClaim bookingDetails={item?.BookingDetails} onClick={props.onClose} />

      {isXs || SettingsCache.settings.Features.includes('DropDownPanelNavigation') ? (
        <P2GDropdown
          id="View Details:"
          onChange={changeTab}
          options={[
            { value: 'ORDER-ACTIONS', text: t('ACTIONS_TAB', { defaultValue: 'Actions' }) },
            {
              value: 'ORDER-DETAILS',
              text: t('ORDER_DETAILS_TAB', { defaultValue: 'Order Details' })
            },
            ...(SettingsCache.settings.Features.includes('Tracking')
              ? [{ value: 'ORDER-TRACKING', text: t('TRACKING_TAB', { defaultValue: 'Tracking' }) }]
              : [])
          ]}
          value={props.editType}
        />
      ) : (
        <P2GButtonGroup fullWidth>
          <P2GButtonGroupItem value="ORDER-ACTIONS" selected={props.editType} onClick={changeTab}>
            {t('ACTIONS_TAB', { defaultValue: 'Actions' })}
          </P2GButtonGroupItem>
          <P2GButtonGroupItem value="ORDER-DETAILS" selected={props.editType} onClick={changeTab}>
            {t('DETAILS_TAB', { defaultValue: 'Details' })}
          </P2GButtonGroupItem>
          {SettingsCache.settings.Features.includes('Tracking') && (
            <P2GButtonGroupItem value="ORDER-TRACKING" selected={props.editType} onClick={changeTab}>
              {t('TRACKING_TAB', { defaultValue: 'Tracking' })}
            </P2GButtonGroupItem>
          )}
        </P2GButtonGroup>
      )}

      {trackingNumber && (
        <SidePanelSection title={t('TRACKING_NUMBER_SUBTITLE', { defaultValue: 'Tracking Number' })}>
          <P2GText>
            {trackingNumber} <SelectOrCopy code={trackingNumber} />
          </P2GText>
        </SidePanelSection>
      )}

      {props.editType === 'ORDER-LABELS' && <OrderLabelsPanel item={props.item} editType={props.editType} />}

      {props.editType === 'ORDER-DETAILS' && (
        <ItemOrderPanel
          key={item.Id}
          item={item}
          close={props.onClose}
          setAction={setAction}
          loadingConsignment={false}
        />
      )}

      {props.editType === 'ORDER-ACTIONS' && (
        <OrderActionsPanel
          key={item.Id}
          item={item}
          onCancel={props.onCancel}
          onRaiseEnquiry={props.onRaiseEnquiry}
          close={props.onClose}
          buyAgain={() => buyAgain(item)}
          arrangeReturn={() => arrangeReturn(item)}
          setAction={setAction}
          actions={() => {
            return getOrderActions(SmartSendProvider, SmartSendProvider, item.Id).then((r) => {
              return r.success ? r.result : null;
            });
          }}
        />
      )}

      {props.editType === 'ORDER-TRACKING' && (
        <ItemOrderTrackingPanel
          recipientIsoCode={item.Recipient?.CountryIsoCode}
          senderIsoCode={item.Sender?.CountryIsoCode}
          trackingDetails={item.TrackingSummary}
          trackingNumber={GetExternalTrackingNumber(item)}
          getTracking={() => {
            return getOrderTracking(
              item.BookingDetails.Service.Provider,
              item.BookingDetails.Service.AccountId,
              item.Id
            ).then((r) => {
              return r.success ? r.result : { Tracking: [], Trackable: true, Tags: [], Links: {} };
            });
          }}
          bookingDetails={item.BookingDetails}
          close={props.onClose}
        />
      )}
    </SidePanelSections>
  );
}
